import Backbone from 'backbone';
import $ from 'jquery';
import React from 'react';
import ReactDOM from 'react-dom';

import * as OnboardingActions from 'actions/OnboardingActions';
import * as analytics from 'analytics/Analytics';
import AssemblySwitcherView from 'assembly/Switcher';
import {MolbioFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import OnboardingConstants from 'constants/OnboardingConstants';
import FolderItemState from 'filestate/FolderItemState';
import ProteinStatusBar from 'molbio/proteins/ProteinStatusBar';
import ProteinStore from 'molbio/proteins/ProteinStore';
import SequenceStatusBar from 'molbio/SequenceStatusBar';
import * as sequtil from 'molbio/SeqUtil';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import SidebarStore from 'sidebar/SidebarStore';

import TimerView from '../notebook/protocols/timers/TimerView';
import {Sequence} from './Sequence';

type SubelName = 'sequence' | 'protein';

let messageTimeout: number | undefined;

class StatusBarView extends Backbone.View<statusbar.StatusBarState> {
  fileState!: FolderItemState | null;
  sequence?: Sequence; // Only defined when a sequence is open
  assemblySwitcher!: AssemblySwitcherView;
  trayTimerView!: TimerView;

  static initClass(): void {
    this.prototype.el = '#statusbar';
  }

  subels: {[n in SubelName]: string} = {
    sequence: '#sequence-statusbar',
    protein: '#protein-statusbar',
  };

  override events(): Backbone.EventsHash {
    return {
      'click .split': 'toggleSplit',
      'mouseover .split': 'showSplitMessage',
      'mouseout .split': 'hideSplitMessage',
    };
  }

  // Map from key to react render function
  reactSubelsRenderFunctions = {
    /* eslint-disable @typescript-eslint/unbound-method */
    protein: this.updateProteinStatusBar,
    sequence: this.updateSequenceStatusBar,
    /* eslint-enable @typescript-eslint/unbound-method */
  };

  override initialize(options: unknown): void {
    this.toggleMinWidthClasses = this.toggleMinWidthClasses.bind(this);
    this.updateSequenceStatusBar = this.updateSequenceStatusBar.bind(this);
    this.updateProteinStatusBar = this.updateProteinStatusBar.bind(this);
    this.assemblySwitcher = new AssemblySwitcherView();
    // eslint-disable-next-line @typescript-eslint/unbound-method
    SidebarStore.addChangeListener(this.toggleMinWidthClasses);
    this.trayTimerView = new TimerView({el: this.$('.trayTimerView')[0]}).render();
    this.registerListeners();
  }

  showSplitMessage(): void {
    if (this.model.get('splitWorkspace')) {
      statusbar.showOverlayMessage('Join workspace panes');
    } else {
      statusbar.showOverlayMessage('Split workspace into two panes');
    }
  }

  hideSplitMessage(): void {
    statusbar.hideOverlayMessage();
  }

  handleModelChange(): void {
    // For reasons that remain unclear (presumably some kind of race condition), this handler can wind up
    // called when there are no changed attributes. We need to catch this case explicitly to prevent errors.
    if (this.model.changedAttributes() === false) {
      return;
    }

    if (this.model.changedAttributes().timeoutMessage) {
      const message = this.model.get('timeoutMessage')!;
      this.$el.find('.timeout').text(message).hide().removeClass('hidden').fadeIn('fast');
      clearTimeout(messageTimeout);
      messageTimeout = setTimeout(() => this.model.unset('timeoutMessage'), 2000) as unknown as number;
    } else {
      // Either it was something other than the timeout message changing
      // OR the timeout message was unset (to undefined)
      // eslint-disable-next-line benchlint/no-in-operator
      if (!('timeoutMessage' in this.model.changedAttributes())) {
        this.model.unset('timeoutMessage');
      }
      this.$el.find('.timeout').addClass('hidden');
      clearTimeout(messageTimeout);
    }
  }

  renderOverlayMessage(): void {
    const message = this.model.get('overlayMessage');
    if (message) {
      this.$el.find('.overlay').text(message).removeClass('hidden');
    } else {
      this.$el.find('.overlay').text('').addClass('hidden');
    }
  }

  toggleSplit(e: MouseEvent): void {
    $('.kill-on-split .js-help-exit-early, .kill-on-split .js-help-done').click();
    const newIsWorkspaceSplit = !this.model.get('splitWorkspace');
    this.fileState!.setIsWorkspaceSplit(newIsWorkspaceSplit);
    this.fileState!.updateWorkspaceSplitPreference(newIsWorkspaceSplit);
    analytics.track('editor', 'toggled_split_workspace', {split: newIsWorkspaceSplit});
    OnboardingActions.completeOnboardingTask(
      OnboardingConstants.MODULES.SEQUENCE,
      OnboardingConstants.SPLIT_WORKSPACE
    );
    this.showSplitMessage();
  }

  toggleMinWidthClasses(): void {
    $('#editor').toggleClass('hasFileBrowserOpen', SidebarStore.getIsOpen());
  }

  renderSplitWorkspaceButton(): void {
    const disabled = this.model.get('numBuffers') === 1;
    this.$el
      .find('.split')
      .toggleClass('active', this.model.isWorkspaceSplit())
      .toggleClass('disabled', disabled)
      .prop('disabled', disabled);
  }

  commonStatusbarSetup(): void {
    /* eslint-disable @typescript-eslint/unbound-method */
    this.listenTo(this.model, 'change', this.handleModelChange);
    this.listenTo(this.model, 'change:overlayMessage', this.renderOverlayMessage);
    this.listenTo(this.model, 'change:splitWorkspace', this.renderSplitWorkspaceButton);
    /* eslint-enable @typescript-eslint/unbound-method */
    this.renderOverlayMessage();
    this.renderSplitWorkspaceButton();
  }

  updateSequenceStatusBar(): void {
    (this.model as statusbar.SequenceStatusBarState).set({
      numBases: this.sequence!.get('dna').forward().length,
    });

    const [selectionStart, selectionEnd] = sequtil.modelRangeToDisplay(
      (this.model as statusbar.SequenceStatusBarState).get('selectionStart'),
      (this.model as statusbar.SequenceStatusBarState).get('selectionEnd'),
      this.sequence!.length(),
      this.sequence!.startIndex()
    );

    renderToDOM(
      <SequenceStatusBar
        numBases={(this.model as statusbar.SequenceStatusBarState).get('numBases')}
        selectionStart={selectionStart}
        selectionEnd={selectionEnd}
        selectionLength={(this.model as statusbar.SequenceStatusBarState).get('selectionLength')}
        selectionGC={(this.model as statusbar.SequenceStatusBarState).get('selectionGC')}
        selectionMeltingTmp={(this.model as statusbar.SequenceStatusBarState).get('selectionMeltingTmp')}
        isOligo={this.sequence!.isOligo()}
        nucleotideType={this.sequence!.nucleotideType()}
      />,
      this.$(this.subels.sequence)[0]
    );
  }

  updateAssemblySwitcher(): void {
    const showSwitcher =
      isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE}) &&
      this.fileState &&
      this.fileState.fileType() === ModelTypeConstants.SEQUENCE &&
      !(this.fileState.model() as unknown as Sequence).isOligo() &&
      !(this.fileState.model() as unknown as Sequence).isRNASequence();
    this.assemblySwitcher.$el.toggleClass('hidden', !showSwitcher);
  }

  showSubel(targetSubel: SubelName): void {
    for (const name of Object.keys(this.subels)) {
      const s = this.subels[name as SubelName];
      $(s).toggleClass('hidden', targetSubel !== name);

      if (targetSubel === name) {
        this.reactSubelsRenderFunctions[name]();
      } else {
        ReactDOM.unmountComponentAtNode(this.$(s)[0]);
      }
    }
  }

  hideSubels(): void {
    for (const name of Object.keys(this.subels)) {
      const s = this.subels[name as SubelName];
      $(s).addClass('hidden');
      ReactDOM.unmountComponentAtNode(this.$(s)[0]);
    }
  }

  updateProteinStatusBar(): void {
    const proteinModel = this.fileState!.model();
    const proteinId = proteinModel.id;
    const protein = ProteinStore.getProteinById(proteinId)!;

    renderToDOM(
      <ProteinStatusBar aminoAcids={protein.version.aminoAcids} proteinId={protein.id} />,
      this.$(this.subels.protein)[0]
    );
  }

  registerListeners(): void {
    ProteinStore.addChangeListener(() => {
      if (this.fileState && this.fileState.fileType() === ModelTypeConstants.PROTEIN) {
        this.updateProteinStatusBar();
      }
    });
  }

  update(fileState: FolderItemState | null): void {
    this.fileState = fileState;
    const statusBarState = this.fileState?.get('statusbar');
    clearTimeout(messageTimeout);
    this.updateAssemblySwitcher();

    // Update internal state
    statusbar.setState(statusBarState);
    if (this.model) {
      this.model.unset('timeoutMessage'); // Old timeout message should not persist
      this.model.unset('overlayMessage');
      this.stopListening(this.model);
    }

    this.model = statusBarState;

    // Hide/show if statusbar was given.
    this.$el.toggleClass('hidden', !statusBarState);
    $('#editor').toggleClass('has-hidden-statusbar', !statusBarState);
    if (!this.fileState || !statusBarState) {
      return;
    }

    // Render from new state.
    if (this.fileState.fileType() === ModelTypeConstants.SEQUENCE) {
      this.sequence = this.fileState.model() as unknown as Sequence;
      this.showSubel('sequence');

      /* eslint-disable @typescript-eslint/unbound-method */
      this.listenTo(
        this.model,
        'change:numBases change:selectionStart change:selectionEnd change:selectionGC change:selectionMeltingTmp',
        this.updateSequenceStatusBar
      );
      /* eslint-enable @typescript-eslint/unbound-method */
    } else if (this.fileState.fileType() === ModelTypeConstants.PROTEIN) {
      this.showSubel('protein');
    } else {
      this.hideSubels();
    }
    this.commonStatusbarSetup();
  }
}
StatusBarView.initClass();

export default StatusBarView;
